import useSWR from "swr";
import axios, { endpoints, fetcher } from "src/utils/axios";
import type {
  FederationOrganisation,
  OrganisationForm,
  OrganisationList,
  OrganisationOptions,
  AssociationAffiliation,
  AssociationRegistrationOptions,
} from "src/types/federation-organisation";

const base = endpoints.provider.federation.organisations;
const memberships = endpoints.provider.federation.memberships;

export function organisationErrorKey(error: unknown) {
  const value = error as {
    detail?: unknown;
    response?: { data?: { detail?: unknown } };
  } | null;
  const detail = value?.detail ?? value?.response?.data?.detail;
  if (detail === "association_verified_account_required")
    return "federation_assoc_verify_account";
  if (detail === "association_provider_required")
    return "federation_assoc_type_required";
  if (typeof detail === "string" && detail.includes("permission_denied"))
    return "federation_decisions_denied";
  if (
    typeof detail === "string" &&
    (detail.includes("conflict") || detail.includes("pending"))
  )
    return "federation_decisions_conflict";
  return "federation_decisions_error";
}

export function useFederationOrganisations(
  providerId: number,
  federation: boolean,
  enabled: boolean,
  offset = 0,
  search = "",
  effectiveAt = "",
) {
  return useSWR<OrganisationList>(
    providerId > 0 && enabled
      ? [
          federation ? base(providerId) : memberships(providerId),
          {
            params: {
              offset,
              limit: 20,
              ...(federation
                ? {
                    search,
                    ...(effectiveAt ? { effective_at: effectiveAt } : {}),
                  }
                : {}),
            },
          },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export function useFederationOrganisation(
  providerId: number,
  id: number | null,
  enabled: boolean,
) {
  return useSWR<FederationOrganisation>(
    providerId > 0 && id && enabled ? `${base(providerId)}/${id}` : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export function useOrganisationOptions(providerId: number, enabled: boolean) {
  return useSWR<OrganisationOptions>(
    providerId > 0 && enabled ? `${memberships(providerId)}/options` : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export async function applyOrganisation(
  clubId: number,
  providerId: number,
  form: OrganisationForm,
  kind: "membership" | "master_data",
  key: string,
) {
  const { data } = await axios.post<FederationOrganisation>(
    `${memberships(clubId)}/${providerId}/applications`,
    { form, kind, idempotency_key: key },
  );
  return data;
}

export async function organisationCommand(
  providerId: number,
  id: number,
  operation: string,
  payload: Record<string, unknown>,
) {
  const { data } = await axios.post<FederationOrganisation>(
    `${base(providerId)}/${id}/${operation}`,
    payload,
  );
  return data;
}

export function useAssociationAffiliations(
  providerId: number,
  enabled: boolean,
) {
  return useSWR<AssociationAffiliation[]>(
    providerId > 0 && enabled
      ? `${memberships(providerId)}/affiliations`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}

export function useAssociationRegistrationOptions() {
  return useSWR<AssociationRegistrationOptions>(
    "/providers/federation-memberships/registration-options",
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}

export async function registerAssociation(
  federationId: number,
  payload: {
    association_provider_id?: number;
    create_new?: boolean;
    form: OrganisationForm;
    idempotency_key: string;
  },
) {
  const { data } = await axios.post<FederationOrganisation>(
    `/providers/${federationId}/federation/association-applications`,
    payload,
  );
  return data;
}

export async function convertAssociation(
  providerId: number,
  expectedType: string,
  key: string,
) {
  const { data } = await axios.post<{
    provider_id: number;
    provider_type: string;
  }>(`${memberships(providerId)}/convert-to-association`, {
    expected_type: expectedType,
    confirm_association: true,
    idempotency_key: key,
  });
  return data;
}
